// =============================================================================
// Marksense v2 — mark-zone config validation + republish-invalidation predicate
// (design: docs/superpowers/specs/2026-07-06-marksense-handwriting-wiring-design.md
// §3/§5; contract: OCR-INTEGRATION-GUIDE §Marksense).
// =============================================================================
/**
 * Stable per-field identity (`internalName ?? name`) — THE zone id contract.
 * Load-bearing across the marksense feature: the worker mints `/api/scan/marks`
 * zone ids with it, the baseline feedback keys `(captureProfileId, zoneId)`
 * with it, and this module names fields in the publish gate that guards those
 * same zones. One exported definition so the copies can never drift (drift
 * silently orphans OCR's learned baselines).
 */
export function fieldContentName(f) {
    return f.internalName ?? f.name;
}
const fieldName = fieldContentName;
/**
 * Validate a form's mark-zone declarations. Pure; returns issues (empty =
 * clean). Enforced as a HARD publish gate for `level: 'error'` issues:
 *  - a zone's `markGroupId` must reference a declared `markGroups` entry
 *  - a zone with `markGroupId` must also declare `markKind`
 *  - all zones in one group must share the same kind (`addedInkDensity` is
 *    not comparable across kinds — mixing skews the group argmax/margin)
 *  - warning: an ExactlyOne/AtMostOne group with <2 member zones
 */
export function validateMarkZoneConfig(form) {
    const issues = [];
    const declaredGroups = new Map((form.markGroups ?? []).map((g) => [g.id, g]));
    const groupKinds = new Map();
    const groupCounts = new Map();
    for (const f of form.fields) {
        const zone = f.zoneDefinition;
        if (!zone)
            continue;
        const { markKind, markGroupId } = zone;
        if (!markKind && !markGroupId)
            continue;
        if (markGroupId && !markKind) {
            issues.push({
                level: 'error',
                fieldName: fieldName(f),
                groupId: markGroupId,
                message: `Zone "${fieldName(f)}" is assigned to mark group "${markGroupId}" but declares no mark kind.`,
            });
        }
        if (markGroupId) {
            if (!declaredGroups.has(markGroupId)) {
                issues.push({
                    level: 'error',
                    fieldName: fieldName(f),
                    groupId: markGroupId,
                    message: `Zone "${fieldName(f)}" references undeclared mark group "${markGroupId}".`,
                });
            }
            groupCounts.set(markGroupId, (groupCounts.get(markGroupId) ?? 0) + 1);
            if (markKind) {
                const kinds = groupKinds.get(markGroupId) ?? new Set();
                kinds.add(markKind);
                groupKinds.set(markGroupId, kinds);
            }
        }
    }
    for (const [groupId, kinds] of groupKinds) {
        if (kinds.size > 1) {
            issues.push({
                level: 'error',
                groupId,
                message: `Mark group "${groupId}" mixes zone kinds (${[...kinds].join(', ')}) — all zones in a group must share one kind: added ink density is not comparable across kinds, so mixing skews the group's winner/margin resolution.`,
            });
        }
    }
    for (const [id, group] of declaredGroups) {
        if ((group.mode === 'ExactlyOne' || group.mode === 'AtMostOne') &&
            (groupCounts.get(id) ?? 0) < 2) {
            issues.push({
                level: 'warning',
                groupId: id,
                message: `Mark group "${id}" (${group.mode}) has fewer than 2 member zones — group resolution is trivial.`,
            });
        }
    }
    return issues;
}
/** Geometry key for a mark zone — the part whose change invalidates learned baselines. */
function markZoneGeometry(form) {
    const out = new Map();
    for (const f of form.fields) {
        const z = f.zoneDefinition;
        if (!z?.markKind)
            continue;
        out.set(fieldName(f), `${z.pageNumber}|${z.x}|${z.y}|${z.width}|${z.height}`);
    }
    return out;
}
/**
 * True when a previously-published mark zone MOVED, RESIZED, or was REMOVED —
 * i.e. when OCR's learned per-zone empty baselines for this form's capture
 * profile are stale and must be deleted (stale baselines from an old layout
 * are worse than none). Purely-added zones return false: no baselines exist
 * for a new zone id yet. Kind-only changes with identical geometry return
 * false: the baseline is a density measurement of the same rectangle.
 */
export function markZonesChanged(prevForm, nextForm) {
    if (!prevForm)
        return false;
    const prev = markZoneGeometry(prevForm);
    if (prev.size === 0)
        return false;
    const next = markZoneGeometry(nextForm);
    for (const [zoneId, geometry] of prev) {
        const nextGeometry = next.get(zoneId);
        if (nextGeometry === undefined || nextGeometry !== geometry)
            return true;
    }
    return false;
}
//# sourceMappingURL=mark-zones.js.map